import {
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import type { Performer } from 'api-client';

function externalUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value, window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function PerformerProfile({ performer }: { performer: Performer }) {
  const hometown: unknown = performer.hometown;
  const location =
    hometown && typeof hometown === 'object'
      ? ['city', 'state', 'country']
          .map((key) => Reflect.get(hometown, key))
          .filter((part) => typeof part === 'string')
          .join(', ')
      : '';
  const social: unknown = performer.socialMedia;
  const links: Array<[string, unknown]> = [
    ['Website', performer.website],
    ...(social && typeof social === 'object' ? Object.entries(social) : []),
  ];
  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h4" component="h1">
              {performer.performerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shared artist profile. Viewing this profile does not add it to
              your team’s performers.
            </Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
              {performer.primaryCategory && (
                <Chip label={performer.primaryCategory} />
              )}
              {performer.tags?.map((tag) => (
                <Chip key={tag} label={tag} variant="outlined" />
              ))}
            </Stack>
            {location && <Typography>{location}</Typography>}
            <Typography
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            >
              {performer.bio || 'No biography added yet.'}
            </Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
              {links.map(([label, value]) => {
                const href = externalUrl(value);
                return value && href ? (
                  <Link
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label}
                  </Link>
                ) : null;
              })}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      {(
        [
          ['Technical requirements', performer.technicalRider],
          ['Hospitality requirements', performer.hospitalityRider],
        ] as const
      ).map(([label, value]) => (
        <Card key={label}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {label}
            </Typography>
            <Typography
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            >
              {value || 'Not provided yet.'}
            </Typography>
          </CardContent>
        </Card>
      ))}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Media
          </Typography>
          {performer.media.length ? (
            <Stack spacing={1}>
              {performer.media.map((media) => {
                const href = externalUrl(media.externalUrl || media.file);
                return href ? (
                  <Link
                    key={media.id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {media.title || media.mediaType || 'View media'}
                  </Link>
                ) : null;
              })}
            </Stack>
          ) : (
            <Typography color="text.secondary">No media added yet.</Typography>
          )}
        </CardContent>
      </Card>
      <Typography variant="body2" color="text.secondary">
        Bookings with your team: {performer.bookingCount}
      </Typography>
    </Stack>
  );
}
